import React, {useEffect, useState} from 'react';
import {IOnlineCondolenceSummary} from '../../types/resources/OnlineCondolenceSummary';
import {ViewMode} from '../../enums/ViewMode';
import {SortMode} from '../../enums/SortMode';
import PaginatedCollection from '../PaginatedCollection';
import OnlineCondolenceCard from './OnlineCondolenceCard';
import {useTranslation} from 'react-i18next';
import {ReactComponent as ListIcon} from '../../icons/list.svg';
import {ReactComponent as TableIcon} from "../../icons/cells.svg";
import styled from 'styled-components';
import OnlineCondolenceListItem from './OnlineCondolenceListItem';
import {useApiService} from '../../context/api/useApiContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import {isMobile} from 'react-device-detect';

const OverviewHeader = styled.div`
`;

const Select = styled.select`
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='9.543' height='7.422' viewBox='0 0 9.543 7.422'%3e%3cpath id='Polygon_1' data-name='Polygon 1' d='M4.771,0,9.543,7.422H0Z' transform='translate(9.543 7.422) rotate(180)' fill='currentColor'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 1em center;
    background-size: var(--cr-text-xs);

    &&& {
        padding-right: 2.25em;
    }
`;

const OnlineCondolenceCollection = () => {
    const { t } = useTranslation();
    const apiService = useApiService();

    const [
        onlineCondolences,
        setOnlineCondolences
    ] = useState<IOnlineCondolenceSummary[]>([]);

    // const [viewMode, setViewMode] = useLocalStorage<ViewMode>('overview/viewMode', ViewMode.GridView);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.GridView);
    // const [sortMode, setSortMode] = useLocalStorage<SortMode>('overview/sortMode', SortMode.DateDescending);
    const [sortMode, setSortMode] = useState<SortMode>(SortMode.DateDescending);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    useEffect(() => {
        const fetchOnlineCondolences = async () => {
            const data = await apiService.getOnlineCondolences();
            const sortedData = getSortedOnlineCondolences(data, sortMode);

            setOnlineCondolences(sortedData);
            setIsLoaded(true);
        }

        fetchOnlineCondolences();
    }, []);

    const getSortedOnlineCondolences = (onlineCondolences: IOnlineCondolenceSummary[], mode: SortMode): IOnlineCondolenceSummary[] => {
        // Sort people based on dateOfDeath.
        return [...onlineCondolences].sort(
            (
                a,
                b
            ) => {
                const dateOfDeathA = a.dateOfDeath ? a.dateOfDeath.getTime() : -Infinity;
                const dateOfDeathB = b.dateOfDeath ? b.dateOfDeath.getTime() : -Infinity;

                if (mode === SortMode.DateAscending) {
                    return dateOfDeathA - dateOfDeathB;
                } else {
                    return dateOfDeathB - dateOfDeathA;
                }
            });
    }

    useEffect(() => {
        const sortedOnlineCondolences = getSortedOnlineCondolences(onlineCondolences, sortMode);
        setOnlineCondolences(sortedOnlineCondolences);
    }, [sortMode]);

    const handleSortModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSortMode = event.target.value as SortMode;

        if (sortMode !== newSortMode) {
            setSortMode(newSortMode);
        }
    }

    const toggleViewMode = () => {
        if (viewMode === ViewMode.GridView) {
            setViewMode(ViewMode.ListView);
        } else {
            setViewMode(ViewMode.GridView);
        }
    };

    const renderCard = (summary: IOnlineCondolenceSummary, index: number) => (
        <OnlineCondolenceCard
            key={index}
            summary={summary}
        />
    );

    const renderListItem = (summary: IOnlineCondolenceSummary, index: number) => (
        <OnlineCondolenceListItem
            key={index}
            summary={summary}
        />
    );

    const hasItems = (onlineCondolences.length > 0);

    return (
        <div className="cr-container">
            <OverviewHeader className="cr-flex cr-justify-between cr-my-xl">
                <div>
                    <label
                        htmlFor="sortModeDropdown"
                        className="cr-mr cr-text-primary"
                    >
                        {t('filtering')}:
                    </label>
                    <Select
                        id="sortModeDropdown"
                        value={sortMode}
                        onChange={handleSortModeChange}
                        className="cr-pill cr-text-primary cr-text-base"
                    >
                        {Object.values(SortMode).map(mode =>
                            <option key={mode} value={mode}>{t(mode)}</option>
                        )}
                    </Select>
                </div>
                {!isMobile &&
                    <button
                        onClick={toggleViewMode}
                        className="cr-flex cr-center cr-text-primary cr-text-base"
                    >
                        {viewMode === ViewMode.GridView
                            ?
                            <>
                                <ListIcon className="cr-mr cr-text-primary" />
                                {t(ViewMode.ListView)}
                            </>
                            :
                            <>
                                <TableIcon className="cr-mr cr-text-primary" />
                                {t(ViewMode.GridView)}
                            </>
                        }
                    </button>
                }
            </OverviewHeader>
            {hasItems
                ?
                    <PaginatedCollection
                        id={'onlineCondolenceCollection'}
                        items={onlineCondolences as IOnlineCondolenceSummary[]}
                        renderItems={{
                            [ViewMode.GridView]: renderCard,
                            [ViewMode.ListView]: renderListItem,
                        }}
                        itemsPerPage={6}
                        itemsPerRow={3}
                        itemsPerRowTablet={2}
                        itemsPerRowMobile={1}
                        viewMode={isMobile ? ViewMode.GridView : viewMode}
                        rowGapClass={(!isMobile && viewMode === ViewMode.ListView) ? 'cr-row-gap-lg' : undefined }
                    />
                : isLoaded &&
                    <div className={'cr-flex cr-center cr-m-xxl'}>{t('no-condolences-available')}</div>
                // TODO: Render skeleton while loading
            }
        </div>
    );
}

export default OnlineCondolenceCollection;
